/**
 * Skeleton pieces that stay visible on the staff portal's canvas.
 *
 * WHY THIS EXISTS: on the internal portal `--background` and `--muted` are the
 * same token (`--hsl-light-gray-100`), so the stock `<Skeleton>` (`bg-muted`)
 * is invisible when it sits straight on the page canvas — a loading page that
 * reads as a blank one. Inside a card (`bg-card`) it shows as normal.
 *
 * So a page skeleton is built the way the page is: white `SkeletonPanel`
 * surfaces carrying ordinary `Skeleton` bars, and `CanvasSkeleton` for the
 * few bars that sit on the canvas itself (breadcrumb, title).
 */
import type { HTMLAttributes, ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** A skeleton bar for the page canvas, where `bg-muted` would vanish. */
export function CanvasSkeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <Skeleton className={cn("bg-border", className)} {...props} />;
}

/** The card surface every page section sits on. */
export function SkeletonPanel({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn("rounded-xl border border-border bg-card shadow-sm", className)}>
      {children}
    </div>
  );
}

/**
 * A board's one-line summary ("12 orders waiting · …") while its numbers load.
 * The same 16px line the text sits on, so the lanes below do not move when it
 * fills in.
 */
export function SummaryLineSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex h-4 items-center", className)} aria-hidden>
      <CanvasSkeleton className="h-3 w-80 max-w-full" />
    </div>
  );
}

/**
 * A board toolbar: the search box then one bar per control, on the toolbar's
 * own card. `controls` are width classes, left to right, so each board draws
 * its own toolbar's shape.
 */
export function BoardToolbarSkeleton({
  searchClassName = "min-w-[220px]",
  controls,
}: {
  searchClassName?: string;
  controls: string[];
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2 shadow-sm"
      aria-hidden
    >
      <Skeleton className={cn("h-9 flex-1", searchClassName)} />
      {controls.map((width, index) => (
        <Skeleton key={index} className={cn("h-9", width)} />
      ))}
    </div>
  );
}
